import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatMoney } from "utilities/formatMoney";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import { useDispatch, useSelector } from "react-redux";
import CurrencyToggle from "component/currencyToggle";
import {
    resetTransfer,
    setAmount,
    setCurrency,
    setProvider,
} from "../../../../redux/slices/tradevuTransferSlice";
import currencyPairCheck from "utilities/currencyPairCheck";
import { ArrowRight2, Timer1 } from "iconsax-reactjs";
import { resetBankTransfer } from "../../../../redux/slices/bankTransferSlice";
import { resetUsdTransfer } from "../../../../redux/slices/usdTransferSlice";
import { resetCryptoTransfer } from "../../../../redux/slices/cryptoTransferSlice";
import { resetCryptoFiatTransfer } from "../../../../redux/slices/cryptoFiatTransferSlice";
import { format, InputNumberFormat, unformat } from "@react-input/number-format";
import SelectBankProviderModal from "component/wallet/SelectBankProviderModal";

const SameCurrencyTradevuUser = () => {
    const [amount, setAmountInput] = useState("");
    const [error, setError] = useState("");
    const [provider, setProviderState] = useState(null);
    const [showProviderModal, setShowProviderModal] = useState(false);
    const { business, currency, wallets } = useSelector((state) => state);
    const dispatch = useDispatch();
    const location = useLocation();
    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;
    const navigate = useNavigate();
    const minAmount = 10;
    const transferData = useSelector((state) => ({
        amount: state.amount,
        memo: state.memo,
        invoice: state.invoice,
        otherDocument: state.otherDocument,
        recipient: state.recipient,
        currency: state.currency,
    }));

    useEffect(() => {
        sessionStorage.setItem("previousTransactionRoute", location.pathname);
    }, []);

    useEffect(() => {
        dispatch(resetBankTransfer());
        dispatch(resetUsdTransfer());
        dispatch(resetTransfer());
        dispatch(resetCryptoTransfer());
        dispatch(resetCryptoFiatTransfer());
    }, [dispatch]);

    useEffect(() => {
        setProviderState(null);
    }, [currency]);

    const currentWallet = wallets?.find((wallet) => wallet.currency === currency);
    const providers = useMemo(
        () => (Array.isArray(currentWallet?.providers) ? currentWallet.providers : []),
        [currentWallet]
    );
    const isKES = String(currency || "").toUpperCase() === "KES";
    const hasMultipleProviders = providers.length > 1 && !isKES;

    const selectedProviderBalance = useMemo(() => {
        if (!hasMultipleProviders || !provider) return null;
        const value = Number(provider?.balance);
        return Number.isFinite(value) ? value : null;
    }, [hasMultipleProviders, provider]);

    const balance =
        selectedProviderBalance !== null ? selectedProviderBalance : currentWallet?.balance || 0;
    const numericAmount = parseFloat(amount || "0");
    const isAmountValid = numericAmount >= minAmount;
    const isOverBalance = numericAmount > balance;
    const needsProvider = hasMultipleProviders && !provider;
    const isButtonDisabled = !isAmountValid || isOverBalance || needsProvider;

    const formatted = format(amount);

    const handleContinue = () => {
        dispatch(setAmount(amount));
        dispatch(setCurrency(currency));
        dispatch(setProvider(provider));
        navigate("/payment/send-money/same-currency/tradevu-user/recipient-info");
    };

    return (
        <OnboardingLayout title="Send money" showSkip={false}>
            <div className="md:px-3 max-w-2xl mx-auto mt-10 space-y-10 pb-24 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-2xl font-normal">How much do you want to send?</h2>
                <div className="bg-white rounded-xl p-6">
                    <div className="flex items-center pb-3 text-2xl font-normal leading-none">
                        <span className="mr-2 align-self-start text-[#475467] md:!pt-5 text-normal font-thin">
                            {currencyPairCheck(currency)}
                        </span>
                        <div className="border-b  md:mr-16">
                            <InputNumberFormat
                                type="currency"
                                locales={"en"}
                                step="0.01"
                                inputMode="numeric"
                                className="!shadow-none  font-thin text-3xl md:text-7xl outline-none w-full caret-[#6F00FF] placeholder:text-[#C4C6DE]"
                                placeholder="0"
                                value={formatted}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    const numericValue = parseFloat(input);

                                    setAmountInput(unformat(input));

                                    if (input === "") {
                                        setError("");
                                        return;
                                    }

                                    if (!isNaN(numericValue)) {
                                        if (numericValue > balance) {
                                            setError(
                                                "You don’t have enough funds for this transfer"
                                            );
                                        } else {
                                            setError("");
                                        }
                                    }
                                }}
                            />
                        </div>

                        <div className="relative min-w-[0px] flex justify-end mt-3">
                            <CurrencyToggle
                                className="w-full text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                                fetchWallet={true}
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel={true}
                            />
                        </div>
                    </div>

                    <div className="flex ml-2 md:ml-6 justify-between mb-4 md:mb-8">
                        <p className="text-[12px] text-[#6F739B] font-[400]">
                            Minimum amount is {currencyPairCheck(currency)}
                            {formatMoney(minAmount)}
                        </p>
                        <span className="text-[12px] text-[#6F739B] font-[400]">
                            Balance: {currencyPairCheck(currency)}
                            {formatMoney(balance)}
                        </span>
                    </div>

                    {error && (
                        <p className="text-[14px] text-[#C01515] font-[350]">
                            You don’t have enough funds for this transfer
                        </p>
                    )}

                    {hasMultipleProviders && (
                        <div className="flex justify-between items-center border-t border-[#E5E7EB] py-3">
                            <p className="text-[#161827] font-[450] text-[16px]">
                                {provider ? (
                                    <>
                                        Sending via {provider.bankName || provider.bank || "—"} (
                                        {provider.accountNumber || "—"})
                                    </>
                                ) : (
                                    "No bank account selected"
                                )}
                            </p>

                            <button
                                onClick={() => setShowProviderModal(true)}
                                className="text-[#7A27FF] font-[450] text-[16px] flex items-center gap-1"
                            >
                                {provider ? "Change bank" : "Select bank"}
                                <ArrowRight2 size={18} />
                            </button>
                        </div>
                    )}

                    <div className="text-sm text-[#161827] space-y-2">
                        <div className="flex justify-between font-normal">
                            <span>Transfer fee (Free)</span>
                            <span className="text-[#667085]">{currencyPairCheck(currency)}0</span>
                        </div>
                        <div className="flex justify-between font-normal">
                            <span>Total to deduct</span>
                            <span>
                                {currencyPairCheck(currency)}
                                {formatMoney(amount) || "0"}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 text-sm text-[#6B7280]"></div>
                </div>
                <div className="hidden md:flex justify-between items-center mt-6">
                    <div className="flex items-center gap-2">
                        <Timer1 size="22" color="#6F739B" /> Sent instantly
                    </div>

                    <div className="flex gap-5">
                        <button onClick={() => navigate(-1)} className="text-[#667085] font-medium">
                            Back
                        </button>
                        <button
                            onClick={handleContinue}
                            disabled={isButtonDisabled}
                            title={needsProvider ? "Select a bank account" : ""}
                            className={`rounded-lg px-6 py-2 font-semibold text-white transition ${
                                !isButtonDisabled
                                    ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                    : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                            }`}
                        >
                            Continue
                        </button>
                    </div>
                </div>

                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text-[15px] font-[450]">Sent instantly</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => navigate(-1)}
                                className="text-[#161827] font-medium w-[40%]"
                            >
                                Back
                            </button>

                            <button
                                onClick={handleContinue}
                                disabled={isButtonDisabled}
                                title={needsProvider ? "Select a bank account" : ""}
                                className={`h-12 rounded-xl px-5 font-semibold text-white transition w-[48%] ${
                                    !isButtonDisabled
                                        ? "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                        : "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                                }`}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectBankProviderModal
                isOpen={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onSelect={(p) => setProviderState(p)}
                selectedId={provider?._id || provider?.accountNumber}
                providers={providers}
                currency={currency}
            />
        </OnboardingLayout>
    );
};

export default SameCurrencyTradevuUser;
